/**
 * TempPasswordDisplay - a new account's temporary password, with a Copy button.
 *
 * Atomic Design role: Molecule (code readout + Button atom). Shown on every
 * "user created" confirmation — the header's Add User modal and the hours
 * editor's Quick Add User summary — so the creator can share the password if
 * the welcome email goes astray. Owns its own brief "Copied!" feedback.
 *
 * @example
 * <TempPasswordDisplay value={result.tempPassword} label="Temporary password" helperText="They must change it on first sign-in." />
 */
import React, { useEffect, useState } from 'react';
import { Button } from '../../atoms/Button';

export interface TempPasswordDisplayProps {
	/** The password to show and copy. */
	value: string;
	/** Optional small uppercase caption above the password. */
	label?: string;
	/** Optional note under the password. */
	helperText?: string;
	/** Optional extra classes on the root. */
	className?: string;
}

export const TempPasswordDisplay: React.FC<TempPasswordDisplayProps> = ({ value, label, helperText, className = '' }) => {
	const [copied, setCopied] = useState(false);

	/* Drop the "Copied!" label after two seconds. */
	useEffect(() => {
		if (!copied) return;
		const timer = window.setTimeout(() => setCopied(false), 2000);
		return () => window.clearTimeout(timer);
	}, [copied]);

	/** Copy to the clipboard. Clipboard can be unavailable (insecure context) —
	 *  the password stays on screen for manual copying, so fail quietly. */
	const copy = async () => {
		try {
			await navigator.clipboard.writeText(value);
			setCopied(true);
		} catch {
			/* see above */
		}
	};

	return (
		<div className={`temp-password-display ${className}`.trim()} data-ref="molecule-temp-password-display">
			{label && <p className="mb-4px text-12px font-sans font-semibold uppercase tracking-wider text-brand-text-muted">{label}</p>}
			<div className="flex items-center justify-between gap-12px">
				<code className="text-18px font-bold text-brand-primary break-all">{value}</code>
				<Button variant="secondary" size="sm" onClick={copy}>{copied ? 'Copied!' : 'Copy'}</Button>
			</div>
			{helperText && <p className="mt-8px text-12px font-sans text-brand-text-muted">{helperText}</p>}
		</div>
	);
};

export default TempPasswordDisplay;
